import { z } from 'zod';
import { useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdatePermission } from '../hooks/useUpdatePermission';

const permissionSchema = z.object({
  module: z.string().min(1, 'Module name is required').trim(),
  actions: z
    .array(z.enum(['view', 'create', 'edit', 'delete', 'approve', 'export', 'import']))
    .min(1, 'At least one action is required'),
  description: z.string().trim().optional(),
  isActive: z.boolean().optional(),
});

const actionOptions = [
  { value: 'view', label: 'View', color: 'blue' },
  { value: 'create', label: 'Create', color: 'green' },
  { value: 'edit', label: 'Edit', color: 'yellow' },
  { value: 'delete', label: 'Delete', color: 'red' },
  { value: 'approve', label: 'Approve', color: 'purple' },
  { value: 'export', label: 'Export', color: 'cyan' },
  { value: 'import', label: 'Import', color: 'indigo' },
];

const EditPermissionModal = (props) => {
  const { isEditModalOpen, setIsEditModalOpen, permission } = props;
  const { mutate: updatePermission, isPending } = useUpdatePermission();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(permissionSchema),
    mode: 'onChange',
  });

  const selectedActions = watch('actions') || [];

  useEffect(() => {
    if (permission) {
      reset({
        module: permission.module,
        actions: permission.actions || [],
        description: permission.description || '',
        isActive: permission.isActive,
      });
    }
  }, [permission, reset]);

  const onSubmit = (data) => {
    if (permission?._id) {
      updatePermission(
        { permissionId: permission._id, ...data },
        {
          onSuccess: () => {
            reset();
            setIsEditModalOpen();
          },
        }
      );
    }
  };

  const getActionColor = (action) => {
    const option = actionOptions.find((opt) => opt.value === action);
    const colors = {
      blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      green: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
      yellow: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
      red: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
      purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
      cyan: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
      indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
    };
    return colors[option?.color] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20';
  };

  if (!isEditModalOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <div>
            <h2 className='text-lg font-semibold'>Edit Permission</h2>
            {permission?.slug && <p className='text-xs text-muted-foreground mt-1'>Slug: {permission.slug}</p>}
          </div>
          <button onClick={() => setIsEditModalOpen(false)} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>
          {/* Module Name */}
          <div>
            <label className='block text-sm font-medium mb-1'>
              Module Name <span className='text-destructive'>*</span>
            </label>
            <input type='text' {...register('module')} className='w-full px-3 py-2 text-sm' />
            {errors.module && <p className='text-destructive text-xs mt-1'>{errors.module.message}</p>}
            <p className='text-xs text-muted-foreground mt-1'>Changing module name will regenerate the slug</p>
          </div>

          {/* Actions Selection */}
          <div>
            <label className='block text-sm font-medium mb-2'>
              Actions <span className='text-destructive'>*</span>
            </label>
            <div className='grid grid-cols-2 gap-3'>
              {actionOptions.map((action) => (
                <label
                  key={action.value}
                  className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedActions.includes(action.value)
                      ? `${getActionColor(action.value)} border-current`
                      : 'border-border hover:bg-accent'
                  }`}
                >
                  <input
                    type='checkbox'
                    value={action.value}
                    {...register('actions')}
                    className='w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary'
                  />
                  <div className='flex-1'>
                    <div className='text-sm font-medium'>{action.label}</div>
                    <div className='text-xs text-muted-foreground capitalize'>{action.value}</div>
                  </div>
                </label>
              ))}
            </div>
            {errors.actions && <p className='text-destructive text-xs mt-1'>{errors.actions.message}</p>}
          </div>

          {/* Selected Actions Preview */}
          {selectedActions.length > 0 && (
            <div className='bg-muted rounded-lg p-4'>
              <label className='block text-sm font-medium mb-2'>Selected Actions Preview</label>
              <div className='flex flex-wrap gap-2'>
                {selectedActions.map((action) => (
                  <span key={action} className={`px-3 py-1 text-xs font-medium rounded ${getActionColor(action)}`}>
                    {action}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className='block text-sm font-medium mb-1'>Description</label>
            <textarea {...register('description')} rows={3} className='w-full px-3 py-2 text-sm' />
            {errors.description && <p className='text-destructive text-xs mt-1'>{errors.description.message}</p>}
          </div>

          {/* Status */}
          <div>
            <label className='flex items-center space-x-2 cursor-pointer'>
              <input type='checkbox' {...register('isActive')} className='w-4 h-4 rounded' />
              <span className='text-sm font-medium'>Active</span>
            </label>
            <p className='text-xs text-muted-foreground mt-1'>Inactive permissions cannot be assigned to roles</p>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={() => setIsEditModalOpen(false)}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isPending}
              className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isPending ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2' />
                  Updating...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <Save size={16} className='mr-2' />
                  Update Permission
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPermissionModal;
